import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { ORG_ROLES } from '../services/permissions/constants';
import FolderService from '../services/FolderService';

/**
 * Centralized authorization hook for frontend
 * Provides consistent permission checking across components
 * Returns specific, declarative permissions matching backend AuthorizationService
 *
 * @param {Object} user - Current user object (from props or query)
 * @param {Object} resource - Resource object with userId property (folder, meeting, etc.)
 * @param {Object} resourceUser - Current user's resource-level permission (FolderUser/MeetingUser)
 * @param {string} resourceType - Type of resource ('folder' or 'meeting')
 * @param {Object} options - Optional parameters
 * @param {string} options.folderId - Folder ID (for meeting resources, to check folder-level permissions)
 * @param {string} options.folderUserRole - User's role in the folder (for meeting resources)
 * @returns {Object} Authorization flags with specific permissions
 */
const useAuthorization = (user, resource = null, resourceUser = null, resourceType = null, options = {}) => {
  const { folderId, folderUserRole } = options;

  const [resolvedFolderUserRole, setResolvedFolderUserRole] = useState(folderUserRole || null);

  useEffect(() => {
    if (folderUserRole && folderUserRole !== resolvedFolderUserRole) {
      setResolvedFolderUserRole(folderUserRole);
    }
  }, [folderUserRole, resolvedFolderUserRole]);

  const shouldFetchFolderRole =
    resourceType === 'meeting' && !!folderId && !resolvedFolderUserRole;

  const { data: fetchedFolder, isLoading: isFolderRoleLoading } = useQuery(
    ['folder-user-role', folderId],
    () => FolderService.getFolder({ id: folderId }),
    {
      enabled: shouldFetchFolderRole,
      staleTime: 0,
      refetchOnMount: 'always',
      retry: false,
    }
  );

  const fetchedFolderRole = fetchedFolder?.userRole || fetchedFolder?.data?.userRole || null;

  useEffect(() => {
    if (!resolvedFolderUserRole && fetchedFolderRole) {
      setResolvedFolderUserRole(fetchedFolderRole);
    }
  }, [fetchedFolderRole, resolvedFolderUserRole]);

  // Base role checks
  const isSuperAdmin = useMemo(() => {
    return user?.role === 'super_admin';
  }, [user]);

  const isOrgOwner = useMemo(() => {
    return user?.orgRole?.access === ORG_ROLES.OWNER;
  }, [user]);

  const isResourceOwner = useMemo(() => {
    return resource?.userId === user?.id;
  }, [resource, user]);

  const isFolderOwner = useMemo(() => {
    return !!(folderId && resolvedFolderUserRole === 'owner');
  }, [folderId, resolvedFolderUserRole]);

  const isFolderContributor = useMemo(() => {
    return !!(folderId && resolvedFolderUserRole === 'contributor');
  }, [folderId, resolvedFolderUserRole]);

  // Meeting-specific role flags
  const meetingUserRole = resourceUser?.role;
  const isCreator = useMemo(() => {
    if (!resourceType || resourceType !== 'meeting') return false;
    return (
      meetingUserRole === 'creator' ||
      isResourceOwner ||
      isOrgOwner ||
      isFolderOwner ||
      (!meetingUserRole && isResourceOwner) ||
      resolvedFolderUserRole === 'contributor'
    );
  }, [resourceType, meetingUserRole, isResourceOwner, isOrgOwner, isFolderOwner, resolvedFolderUserRole]);

  const isContributor = useMemo(() => {
    if (!resourceType || resourceType !== 'meeting') return false;
    return !isCreator && meetingUserRole === 'contributor';
  }, [resourceType, isCreator, meetingUserRole]);

  const isContributorClosed = useMemo(() => {
    if (!resourceType || resourceType !== 'meeting') return false;
    return meetingUserRole === 'contributor-closed';
  }, [resourceType, meetingUserRole]);

  const isViewer = useMemo(() => {
    if (!resourceType || resourceType !== 'meeting') return false;
    // Org owners should never be treated as viewers, even if they have viewer subrole
    return !isOrgOwner && meetingUserRole === 'viewer';
  }, [resourceType, meetingUserRole, isOrgOwner]);

  // Folder-specific permissions
  const canManageFolderPermissions = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (!resource || resourceType !== 'folder') return false;
    if (!resourceUser) return false;
    // Only folder owners can manage permissions (contributors removed)
    return isResourceOwner || resourceUser.role === 'owner';
  }, [isSuperAdmin, isOrgOwner, resource, resourceType, resourceUser, isResourceOwner]);

  const canEditFolder = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (!resource || resourceType !== 'folder') return false;
    if (isResourceOwner) return true;
    if (!resourceUser) return false;
    // Folder owners and creators can edit
    return resourceUser.role === 'owner' || resourceUser.role === 'creator';
  }, [isSuperAdmin, isOrgOwner, resource, resourceType, isResourceOwner, resourceUser]);

  const canDeleteFolder = useMemo(() => {
    // Same logic as canEditFolder
    return canEditFolder;
  }, [canEditFolder]);

  const canCreateSubfolder = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (!resource || resourceType !== 'folder') return false;
    if (isResourceOwner) return true;
    if (!resourceUser) return false;
    // Folder owners and contributors can create subfolders
    return ['owner', 'contributor'].includes(resourceUser.role);
  }, [isSuperAdmin, isOrgOwner, resource, resourceType, isResourceOwner, resourceUser]);

  // Meeting-specific permissions
  const canManageMeetingPermissions = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (resourceType !== 'meeting') return false;

    // Folder owner can manage permissions for meetings in their folder
    // This check doesn't require resource to be loaded
    if (isFolderOwner) {
      return true;
    }

    // Other checks require resource to be available
    if (!resource) return false;

    // Direct owner
    if (isResourceOwner) return true;
    // Meeting creator role
    if (resourceUser?.role === 'creator') return true;

    return false;
  }, [isSuperAdmin, isOrgOwner, resourceType, isFolderOwner, resource, isResourceOwner, resourceUser]);

  const canEditMeeting = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (isFolderOwner) return true;
    if (!resource || resourceType !== 'meeting') return false;
    // Direct owner
    if (isResourceOwner) return true;
    // MeetingUser with contributor or creator role
    if (resourceUser && ['creator', 'contributor'].includes(resourceUser.role)) {
      return true;
    }
    return false;
  }, [isSuperAdmin, isOrgOwner, isFolderOwner, resource, resourceType, isResourceOwner, resourceUser]);

  const canDeleteMeeting = useMemo(() => {
    // Contributors cannot delete meetings - only creators, owners, org owners, and super admins
    if (isSuperAdmin || isOrgOwner) return true;
    if (!resource || resourceType !== 'meeting') return false;
    // Direct owner
    if (isResourceOwner) return true;
    // Only creators can delete (not contributors)
    if (resourceUser && resourceUser.role === 'creator') {
      return true;
    }
    // Folder owner can delete meetings in their folder
    if (isFolderOwner) {
      return true;
    }
    return false;
  }, [isSuperAdmin, isOrgOwner, resource, resourceType, isResourceOwner, resourceUser, isFolderOwner]);

  const canCreateMeeting = useMemo(() => {
    // Super admin and org owners can always create meetings
    if (isSuperAdmin || isOrgOwner) return true;
    
    // For creating meetings, we need folder-level permissions
    // This check works even when resource is null (new meeting creation)
    if (!folderId) return false;
    
    // Check folder-level role: owners and contributors can create meetings
    if (resolvedFolderUserRole && ['owner', 'contributor'].includes(resolvedFolderUserRole)) {
      return true;
    }
    
    return false;
  }, [isSuperAdmin, isOrgOwner, folderId, resolvedFolderUserRole]);

  // Legacy generic permissions (for backward compatibility during migration)
  // These will be removed once all components are updated
  const canManagePermissions = useMemo(() => {
    if (resourceType === 'folder') return canManageFolderPermissions;
    if (resourceType === 'meeting') return canManageMeetingPermissions;
    if (resourceType === 'organization') return isOrgOwner;
    return false;
  }, [resourceType, canManageFolderPermissions, canManageMeetingPermissions, isOrgOwner]);

  const canEdit = useMemo(() => {
    if (resourceType === 'folder') return canEditFolder;
    if (resourceType === 'meeting') return canEditMeeting;
    return false;
  }, [resourceType, canEditFolder, canEditMeeting]);

  const canDelete = useMemo(() => {
    if (resourceType === 'folder') return canDeleteFolder;
    if (resourceType === 'meeting') return canDeleteMeeting;
    return false;
  }, [resourceType, canDeleteFolder, canDeleteMeeting]);

  const canCreate = useMemo(() => {
    if (isSuperAdmin || isOrgOwner) return true;
    if (!resourceUser) return false;
    // Contributors and owners can create
    return ['owner', 'contributor'].includes(resourceUser.role);
  }, [isSuperAdmin, isOrgOwner, resourceUser]);

  const canViewAccessList = useMemo(() => {
    // If user can manage permissions, they can view the access list
    if (resourceType === 'folder' && canManageFolderPermissions) return true;
    if (resourceType === 'meeting' && canManageMeetingPermissions) return true;
    if (resourceType === 'organization' && isOrgOwner) return true;

    // For meetings, folder contributors should also be able to view access lists
    if (resourceType === 'meeting' && isFolderContributor) return true;

    // Contributors can view access list but not manage (requires resourceUser)
    if (!resourceUser) return false;
    return resourceUser.role === 'contributor';
  }, [resourceType, canManageFolderPermissions, canManageMeetingPermissions, isOrgOwner, resourceUser, isFolderContributor]);

  return {
    // Base role checks
    isSuperAdmin,
    isOrgOwner,
    isResourceOwner,
    isFolderOwner,
    isFolderRoleLoading,

    // Folder-specific permissions
    canManageFolderPermissions,
    canEditFolder,
    canDeleteFolder,
    canCreateSubfolder,

    // Meeting-specific permissions
    canManageMeetingPermissions,
    canEditMeeting,
    canDeleteMeeting,
    canCreateMeeting,

    // Meeting-specific role flags
    isCreator,
    isContributor,
    isContributorClosed,
    isViewer,
    meetingUserRole,
    folderUserRole: resolvedFolderUserRole,

    // Legacy generic permissions (for backward compatibility)
    canManagePermissions,
    canEdit,
    canDelete,
    canCreate,
    canViewAccessList,
  };
};

export default useAuthorization;
